// Phase 1.5 item #2 · Management surface hub
//
// Single-page tabbed view for the 4 Phase 1 sub-task H surfaces:
//   Datasets · Prompts · Evals · Annotations
//
// Routes (mounted in Shell.jsx):
//   /analytics/observability             → tab=datasets
//   /analytics/observability/datasets    → DatasetsListPage
//   /analytics/observability/datasets/:id → DatasetDetailPage
//   /analytics/observability/prompts     → PromptsListPage
//   /analytics/observability/prompts/:name → PromptVersionsPage
//   /analytics/observability/evals       → EvalsListPage
//   /analytics/observability/evals/:runId → EvalRunDetailPage
//   /analytics/observability/annotations → AnnotationQueuePage
//
// All endpoints tenant-scoped backend-side via JWT
// (require_administrator / require_owner per spec §6 RBAC matrix).
// No cost numbers rendered (Phase 1 invariant #3).
//
// Backend routes shipped Phase 1 sub-task H; this is the frontend.
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { COLORS } from "../../../theme/colors";
import PageHeader from "../../../components/PageHeader";
import { DatasetsListPage, DatasetDetailPage } from "./DatasetsPages";
import { PromptsListPage, PromptVersionsPage } from "./PromptsPages";
import { EvalsListPage, EvalRunDetailPage } from "./EvalsPages";
import { AnnotationQueuePage } from "./AnnotationsPages";
import HealthPage from "./HealthPage";

const TABS = [
  { id: "datasets",    label: "Datasets",    subtitle: "Pin golden Q&A pairs from real traces · replay them after every prompt or model change." },
  { id: "prompts",     label: "Prompts",     subtitle: "Version-controlled prompt registry with rollout, diff, and rollback." },
  { id: "evals",       label: "Evals",       subtitle: "Dataset runs — per-item scoring, latency, and pass/fail history." },
  // { id: "annotations", label: "Annotations", subtitle: "Human-in-the-loop review queue for LLM outputs and edge cases." },
  { id: "health",      label: "Health",      subtitle: "Watch the watchers — telemetry-backend status + per-agent voice latency (i2.voice.*) in-app." },
];

export default function ObservabilityHub() {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse current sub-route. Path shape:
  //   /analytics/observability/<tab>[/<id>]
  const parts = location.pathname.replace(/^\/analytics\/observability\/?/, "").split("/").filter(Boolean);
  const activeTab = parts[0] || "datasets";
  const detailId = parts[1] || null;

  const RenderedPage = pickPage(activeTab, detailId);
  const activeTabMeta = TABS.find(t => t.id === activeTab) || TABS[0];

  return (
    <div className="panel-scroll" style={{
      flex: 1, padding: "24px 28px", overflowY: "auto",
    }}>
      <PageHeader
        title={activeTabMeta.label}
        subtitle={activeTabMeta.subtitle}
        breadcrumb={[
          {
            label: "Observability",
            onClick: () => navigate("/analytics/observability/datasets"),
          },
          { label: activeTabMeta.label },
        ]}
      />

      {/* Tab bar */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 20,
        borderBottom: `1px solid ${COLORS.border}`,
      }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => navigate(`/analytics/observability/${tab.id}`)}
              style={{
                padding: "10px 20px",
                border: "none", cursor: "pointer", background: "transparent",
                fontSize: 12, fontWeight: 600,
                color: isActive ? COLORS.text : COLORS.textMuted,
                borderBottom: isActive
                  ? `2px solid ${COLORS.accent || "#7c3aed"}`
                  : "2px solid transparent",
                transition: "all 0.2s",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Page body */}
      <RenderedPage detailId={detailId} navigate={navigate} />
    </div>
  );
}

function pickPage(tab, id) {
  switch (tab) {
    case "datasets": return id ? DatasetDetailPage : DatasetsListPage;
    case "prompts":  return id ? PromptVersionsPage : PromptsListPage;
    case "evals":    return id ? EvalRunDetailPage : EvalsListPage;
    case "annotations": return AnnotationQueuePage;
    case "health":   return HealthPage;
    // There is no trace-keyed viewer here — the Inspector is conversation-keyed
    // and lives at /analytics/conversations/:id (eval rows deep-link there via
    // inspectorPathForItem). A stray /analytics/observability/traces[/:id] link
    // (Posture B: no public Langfuse URL) lands on the Evals list rather than
    // silently falling through to Datasets, which is the UI-2 bug this guards.
    case "traces":   return EvalsListPage;
    default:         return DatasetsListPage;
  }
}
